import React, { type FC, useEffect, useRef } from 'react';

import {
  Button,
  Checkbox,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FaRegEnvelope } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { CandidateDeleteBottomSheet } from '@/components/CandidateDeleteBottomSheet';
import {
  DataList,
  DataListCell,
  DataListEmptyState,
  DataListErrorState,
  DataListLoadingState,
  DataListRow,
  DataListTextHeader,
} from '@/components/DataList';
import { ADMIN_PATH } from '@/features/admin';
import { ShareCandidateModal, useShareToken } from '@/features/admin-interview';
import {
  type CandidateBasicDto,
  CandidateSortColumn,
  InterviewType,
  QuestionType,
} from '@/services/api';

import { SortFilter } from '../../components/Filters/Sort/SortFilter';
import {
  IconAlertCircle,
  IconCompare,
  IconEllipseShade,
} from '../../components/Icons';
import { usePageAdminInterviewContext } from '../admin-interview/PageAdminInterview.context';
import { useGetInterviewQuery } from '../admin-interview/useAdminInterviewQueries';
import { CandidateListItems } from './CandidateListItems';
import { type CandidateListProps } from './types';
import { useRemoveCandidateMutation } from './useCandidateTableMutation';

export const CandidateList: React.FC<CandidateListProps> = ({
  setSelectedCandidates,
  candidatesQuery,
  selectedCandidates,
  onCandidateSelect,
  onSelectAll,
  interviewId,
  variant = 'interview',
  sortOrder,
  setSortOrder,
  sortItem,
  setSortItem,
  onDeleteSuccess,
}) => {
  const { questionsData } = usePageAdminInterviewContext();
  const { t } = useTranslation([
    'common',
    'interview',
    'auth',
    'candidate',
    'components',
  ]);
  const deleteCandidateModalDisclosure = useDisclosure();
  const deleteBottomSheetDisclosure = useDisclosure();
  const shareCandidatesDisclosure = useDisclosure();
  const shareToken = useShareToken();
  const navigate = useNavigate();
  const toast = useToast();
  const removeCandidateMutation = useRemoveCandidateMutation({
    onSuccess() {
      setSelectedCandidates([]);
      deleteBottomSheetDisclosure.onToggle();
      onDeleteSuccess();
      toast({ title: t('interview:candidatesDeleted'), status: 'success' });
    },
  });

  const getInterviewQuery = useGetInterviewQuery(
    {
      interviewsId: interviewId!,
    },
    {
      enabled: !!interviewId,
      axiosRequestConfig: {
        overrideSecurityToken: shareToken,
      },
    }
  );

  const {
    data: candidatesPaged,
    isLoading,
    isError,
    refetch,
  } = candidatesQuery;

  const candidates = candidatesPaged?.data ?? [];

  const selectAll =
    candidates.length > 0 && selectedCandidates.length === candidates.length;

  function handleShareCandidates() {
    if (!interviewId) return;

    shareCandidatesDisclosure.onOpen();
  }

  const handleCompare = (selectedCandidates: Array<CandidateBasicDto>) => {
    const candidateIds = selectedCandidates.map((c) => c.id);

    navigate(
      `${ADMIN_PATH}/interviews/${interviewId}/compare?candidateIds=${candidateIds}`
    );
  };

  const noSelection = selectedCandidates.length === 0;

  useEffect(() => {
    if (selectedCandidates.length > 1 && !deleteBottomSheetDisclosure.isOpen) {
      deleteBottomSheetDisclosure.onToggle();
    }
    if (selectedCandidates.length <= 1 && deleteBottomSheetDisclosure.isOpen) {
      deleteBottomSheetDisclosure.onToggle();
    }
  }, [deleteBottomSheetDisclosure, selectedCandidates, setSelectedCandidates]);

  const deleteModalFinalFocusRef = useRef(null);

  return (
    <>
      <DataList>
        <DataListRow>
          <DataListCell w={10} alignItems="center" p={0} borderRight="none">
            <Checkbox
              isChecked={selectAll}
              onChange={(e) => onSelectAll(e.target.checked)}
            />
          </DataListCell>
          {noSelection ? (
            <>
              <DataListCell px={3} flex={2}>
                <DataListTextHeader>
                  {t('common:information.personalInformation')}
                </DataListTextHeader>
              </DataListCell>
              <DataListCell
                flexDir="row"
                align="center"
                justifyContent="flex-start"
                gap={2}
              >
                <DataListTextHeader>
                  {t('common:timeAndDate.uploadDate')}
                </DataListTextHeader>

                <SortFilter<CandidateSortColumn>
                  onSortOrderChange={(newSortOrder, sortColumnAffected) => {
                    setSortOrder(newSortOrder);
                    setSortItem(sortColumnAffected);
                  }}
                  currentSortOrder={sortOrder}
                  activeSortColumn={sortItem}
                  thisFiltersSortColumn={CandidateSortColumn.CreatedAt}
                />
              </DataListCell>
              <DataListCell align="center">
                <DataListTextHeader>
                  {t('common:detail.status')}
                </DataListTextHeader>
              </DataListCell>
              <DataListCell flexDir="row" align="center" gap={2}>
                <DataListTextHeader>
                  {t('common:detail.rating')}
                </DataListTextHeader>

                <SortFilter<CandidateSortColumn>
                  onSortOrderChange={(newSortOrder, sortColumnAffected) => {
                    setSortOrder(newSortOrder);
                    setSortItem(sortColumnAffected);
                  }}
                  currentSortOrder={sortOrder}
                  activeSortColumn={sortItem}
                  thisFiltersSortColumn={CandidateSortColumn.Rating}
                />
              </DataListCell>
              <DataListCell flexDir="row" align="center" gap={2} w="12%">
                <DataListTextHeader>
                  {t('interview:videoInterview.aiScore')}
                </DataListTextHeader>

                <SortFilter<CandidateSortColumn>
                  onSortOrderChange={(newSortOrder, sortColumnAffected) => {
                    setSortOrder(newSortOrder);
                    setSortItem(sortColumnAffected);
                  }}
                  currentSortOrder={sortOrder}
                  activeSortColumn={sortItem}
                  thisFiltersSortColumn={CandidateSortColumn.AiRating}
                />
              </DataListCell>
              <DataListCell w="18%">
                <DataListTextHeader>
                  {t('interview:videoInterview.transactions')}
                </DataListTextHeader>
              </DataListCell>
            </>
          ) : (
            <DataListCell px={2}>
              <HStack>
                <Button
                  h={9}
                  borderRadius="lg"
                  isDisabled={!!shareToken}
                  py="1px"
                  fontSize="sm"
                  leftIcon={<FaRegEnvelope fontSize={18} />}
                  variant="@secondary"
                  onClick={handleShareCandidates}
                >
                  {t('candidate:shareCandidateWithEmail')}
                </Button>

                <Button
                  h={9}
                  borderRadius="lg"
                  isDisabled={!!shareToken}
                  py="1px"
                  fontSize="sm"
                  leftIcon={<IconCompare fontSize={18} />}
                  rightIcon={<>({selectedCandidates.length})</>}
                  variant="@secondary"
                  onClick={() => handleCompare(selectedCandidates)}
                >
                  {t('candidate:compareCandidates')}
                </Button>
              </HStack>
            </DataListCell>
          )}
        </DataListRow>
        {isLoading && <DataListLoadingState />}
        {isError && (
          <DataListErrorState
            title={t('auth:users.feedbacks.loadingUserError.title')}
            retry={refetch}
          />
        )}
        {!isLoading && !isError && candidates.length === 0 && (
          <DataListEmptyState />
        )}
        <Flex
          flexDirection="column"
          position="relative"
          boxShadow="none"
          borderRadius="none"
          h="620px"
          bg="white"
          overflow="auto"
          // style={{
          //   'scrollbar-gutter': 'stable both-edges',
          // }}
        >
          {candidates.map((candidate) => {
            return (
              <CandidateListItems
                variant={variant}
                isShowAiScore={
                  getInterviewQuery.data?.interview?.type !==
                    InterviewType.ExitInterview &&
                  !questionsData?.questions?.every(
                    (el) => el.questionType === QuestionType.Score
                  )
                }
                key={candidate.id ?? Math.random().toString()}
                candidate={candidate}
                isSelected={Boolean(
                  selectedCandidates.find((c) => c.id === candidate.id)
                )}
                onSelect={onCandidateSelect}
              />
            );
          })}
        </Flex>
      </DataList>
      <ShareCandidateModal
        {...shareCandidatesDisclosure}
        interviewId={interviewId}
        setSelectedCandidates={setSelectedCandidates}
        selectedCandidates={selectedCandidates}
        allCandidates={candidates}
      />

      <CandidateDeleteBottomSheet
        isOpen={deleteBottomSheetDisclosure.isOpen}
        onClose={() => {
          setSelectedCandidates([]);
          deleteBottomSheetDisclosure.onClose();
        }}
        selectedCandidates={selectedCandidates.map((s) => s.id)}
        onDeleteCandidates={deleteCandidateModalDisclosure.onToggle}
      />

      <DeleteCandidateModal
        isOpen={deleteCandidateModalDisclosure.isOpen}
        onClose={deleteCandidateModalDisclosure.onClose}
        title={t('components:board.deleteCandidateTitle')}
        subTitle={t('components:board.deleteCandidateListSubTitle')}
        isLoading={removeCandidateMutation.isPending}
        onDelete={async () => {
          if (!interviewId) return;
          await removeCandidateMutation.mutateAsync({
            candidateIds: selectedCandidates.map((s) => s.id),
            interviewsId: interviewId,
          });
        }}
        finalFocusRef={deleteModalFinalFocusRef}
      />
    </>
  );
};

export type DeleteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subTitle: string;
  onDelete: () => Promise<void>;
  isLoading: boolean;
  finalFocusRef?: React.RefObject<HTMLButtonElement>;
};

const DeleteCandidateModal: FC<DeleteModalProps> = ({
  isOpen,
  onClose,
  title,
  subTitle,
  onDelete,
  isLoading,
  finalFocusRef,
}) => {
  const { t } = useTranslation(['common']);

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      finalFocusRef={finalFocusRef}
    >
      <ModalOverlay />
      <ModalContent maxWidth="440px" borderRadius="2xl" p={0}>
        <ModalCloseButton
          borderRadius="full"
          borderWidth={1}
          borderColor="gray.50"
          mt={2.5}
        />
        <ModalHeader justifyItems="center" px={0} mt={3}>
          <Stack px={10} mb={2}>
            <Flex justifyContent="center" position="relative" mt={3}>
              <IconEllipseShade fontSize={64} position="absolute" top={-6} />
              <Flex
                h={12}
                w={12}
                borderWidth="1px"
                borderColor="#EFD3B8"
                borderRadius="50%"
                justifyContent="center"
                alignItems="center"
              >
                <IconAlertCircle color="error.500" fontSize={28} />
              </Flex>
            </Flex>
          </Stack>
          <Text textAlign="center" fontSize="xl" px={12} fontWeight="semibold">
            {title}
          </Text>
        </ModalHeader>

        <ModalBody p={0}>
          <VStack gap={0}>
            <Text my={3} px={10} color="gray.600" textAlign="center">
              {subTitle}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack gap={3} w="full">
            <Button onClick={onClose} flexGrow={1} color="brand.500">
              {t('actions.cancel2')}
            </Button>
            <Button
              isLoading={isLoading}
              flexGrow={1}
              variant="@primary"
              onClick={async () => {
                await onDelete();
                onClose();
              }}
            >
              {t('actions.yes_delete')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
